// Continuous sounds (LoopId). A loop is a Voice whose sources never stop on
// their own; the engine fades it in, exposes volume/pitch through a LoopHandle
// and kills it (with a fade) on stop(). Frequency params that should follow
// setPitch() are registered in `v.pitchParams` with their base value.

import type { LoopId } from '../core/types';
import type { Voice } from './synth';

export interface LoopDef {
  gain: number;
  /** Max simultaneous instances of this loop (the oldest is faded out). */
  max: number;
  reverb?: number;
  build(v: Voice): void;
}

/** Free-running oscillator into `to`, registered for pitch control. */
function drone(v: Voice, type: OscillatorType, f: number, level: number, to: AudioNode, detune = 0): OscillatorNode {
  const g = v.gain(level, to);
  const o = v.osc(type, f, 0, null, g);
  o.detune.value = detune;
  v.pitchParams.push({ param: o.frequency, base: o.frequency.value });
  return o;
}

/** Continuous noise into `to` (playback rate follows pitch). */
function hiss(v: Voice, color: 'white' | 'pink' | 'brown', level: number, to: AudioNode): AudioBufferSourceNode {
  const g = v.gain(level, to);
  const n = v.noiseSrc(color, 0, null, g);
  v.pitchParams.push({ param: n.playbackRate, base: n.playbackRate.value });
  return n;
}

const rainCache = new Map<number, AudioBuffer>();

/**
 * 6 s stereo rain bed: soft pink wash plus independent droplet ticks per
 * channel, cross-faded at the seam so it loops invisibly.
 */
function rainBuffer(ctx: BaseAudioContext): AudioBuffer {
  const cached = rainCache.get(ctx.sampleRate);
  if (cached) return cached;
  const sr = ctx.sampleRate;
  const len = Math.floor(sr * 6);
  const fade = Math.floor(sr * 0.25);
  const buf = ctx.createBuffer(2, len, sr);
  for (let ch = 0; ch < 2; ch++) {
    const raw = new Float32Array(len + fade);
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < raw.length; i++) {
      const w = Math.random() * 2 - 1;
      b0 = 0.99765 * b0 + w * 0.099046;
      b1 = 0.963 * b1 + w * 0.2965164;
      b2 = 0.57 * b2 + w * 1.0526913;
      raw[i] = (b0 + b1 + b2 + w * 0.1848) * 0.05;
    }
    // Droplets: short damped resonances at random pitches.
    const drops = Math.floor(6 * 55);
    for (let k = 0; k < drops; k++) {
      const start = Math.floor(Math.random() * (raw.length - sr * 0.03));
      const f = 1800 + Math.random() * 4200;
      const amp = 0.05 + Math.random() * 0.16;
      const decay = 0.003 + Math.random() * 0.008;
      const w = (2 * Math.PI * f) / sr;
      const n = Math.floor(sr * decay * 5);
      for (let i = 0; i < n; i++) raw[start + i] += Math.sin(w * i) * amp * Math.exp(-i / (sr * decay));
    }
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = raw[i];
    for (let i = 0; i < fade; i++) {
      const k = i / fade;
      d[i] = raw[i] * k + raw[len + i] * (1 - k);
    }
  }
  rainCache.set(ctx.sampleRate, buf);
  return buf;
}

export const LOOPS: Record<LoopId, LoopDef> = {
  /** Unopened loot orb: glassy beating hum with a slow shimmer. */
  orb_hum: {
    gain: 0.22,
    max: 4,
    reverb: 0.35,
    build(v) {
      const body = v.gain(1);
      v.lfo(body.gain, 0.35, 0.25, 0, null);
      drone(v, 'sine', 220, 0.45, body);
      drone(v, 'sine', 221.6, 0.4, body);
      drone(v, 'triangle', 660, 0.08, body, 4);
      const tremolo = v.gain(0.5, v.out);
      const sh = v.gain(1, tremolo);
      v.lfo(tremolo.gain, 0.9, 0.5, 0, null);
      drone(v, 'sine', 1320, 0.06, sh);
      drone(v, 'sine', 1984, 0.035, sh);
    },
  },
  /** Continuous energy beam: buzzy, tremolo'd core plus hiss. */
  beam: {
    gain: 0.3,
    max: 3,
    reverb: 0.15,
    build(v) {
      const core = v.chain({ filter: { type: 'bandpass', f: 1200, q: 1.2 }, dist: 3 });
      const tremolo = v.gain(0.7, core);
      v.lfo(tremolo.gain, 30, 0.3, 0, null);
      drone(v, 'sawtooth', 110, 0.5, tremolo);
      drone(v, 'square', 220.5, 0.3, tremolo);
      drone(v, 'sawtooth', 331, 0.15, tremolo, 7);
      hiss(v, 'white', 0.12, v.filter({ type: 'highpass', f: 4000 }));
      drone(v, 'sine', 55, 0.3, v.out);
    },
  },
  /** Weapon / ability charge-up; callers sweep setPitch() as it fills. */
  charge_hum: {
    gain: 0.25,
    max: 2,
    reverb: 0.15,
    build(v) {
      const lp = v.filter({ type: 'lowpass', f: 900, q: 4 });
      v.pitchParams.push({ param: lp.frequency, base: lp.frequency.value });
      const a = drone(v, 'sawtooth', 110, 0.35, lp);
      const b = drone(v, 'sine', 220, 0.4, v.out);
      v.lfo(a.detune, 6, 14, 0, null);
      v.lfo(b.detune, 6.3, 10, 0, null);
      drone(v, 'triangle', 440, 0.12, v.out, 5);
    },
  },
  /** Boss machinery: low detuned saws through a slowly breathing filter. */
  boss_engine: {
    gain: 0.35,
    max: 1,
    reverb: 0.2,
    build(v) {
      const out = v.gain(0.8);
      v.lfo(out.gain, 2, 0.2, 0, null);
      const drive = v.shaper(3, out);
      const lp = v.filter({ type: 'lowpass', f: 260, q: 3 }, drive);
      v.lfo(lp.frequency, 0.15, 140, 0, null);
      drone(v, 'sawtooth', 41, 0.4, lp);
      drone(v, 'sawtooth', 41.3, 0.4, lp, 6);
      drone(v, 'sawtooth', 61.7, 0.22, lp);
      hiss(v, 'brown', 0.5, v.filter({ type: 'lowpass', f: 160 }));
    },
  },
  /** Ambient rain bed for the Neon Graveyard. */
  rain: {
    gain: 0.35,
    max: 1,
    reverb: 0.1,
    build(v) {
      const lp = v.filter({ type: 'lowpass', f: 6000 });
      const hp = v.filter({ type: 'highpass', f: 280 }, lp);
      const src = v.ctx.createBufferSource();
      src.buffer = rainBuffer(v.ctx);
      src.loop = true;
      src.connect(hp);
      v.source(src, 0, null);
      v.pitchParams.push({ param: src.playbackRate, base: 1 });
      // Distant low rumble under the rain.
      hiss(v, 'brown', 0.25, v.filter({ type: 'lowpass', f: 220 }));
    },
  },
  /** Siege field / zone: warm mains hum with a slowly sweeping resonance. */
  field_hum: {
    gain: 0.25,
    max: 3,
    reverb: 0.3,
    build(v) {
      const body = v.gain(0.8);
      v.lfo(body.gain, 0.5, 0.2, 0, null);
      drone(v, 'sine', 60, 0.4, body);
      drone(v, 'sine', 120, 0.22, body);
      drone(v, 'sawtooth', 180, 0.06, v.filter({ type: 'lowpass', f: 450 }, body));
      const bp = v.filter({ type: 'bandpass', f: 900, q: 5 }, body);
      v.lfo(bp.frequency, 0.2, 350, 0, null);
      hiss(v, 'pink', 0.25, bp);
    },
  },
};
